import { 
  useState, 
  useEffect, 
  useRef 
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container, Table, Col, Row, Button, Spinner,
} from "reactstrap";
import { withTranslation, useTranslation } from "react-i18next";
import { AvField, AvForm } from "availity-reactstrap-validation";
import moment from "moment";
import { fetchDetailedPerformance } from "store/actions";
import { downloadIbDetailedPerformanceAPI } from "apis/forex/ib";
import CardWrapper from "components/Common/CardWrapper";
import CustomPagination from "components/Common/CustomPagination";
import {
  Th,
  Thead,
  Tr,
} from "react-super-responsive-table";

const PRODUCT_OPTIONS = [
  {
    value: "forex",
    label: "Forex",
  },
  {
    value: "ForexMajor",
    label: "Forex Major",
  },
  {
    value: "ForexMinor",
    label: "Forex Minor",
  },
  {
    value: "ForexExotic",
    label: "Forex Exotic",
  },
  {
    value: "metals",
    label: "Metals",
  },
  {
    value: "bullion",
    label: "Bullion",
  },
  {
    value: "commodities",
    label: "Commodities",
  },
  {
    value: "indices",
    label: "Indices",
  },
  {
    value: "energy",
    label: "Energy",
  },
  {
    value: "crypto",
    label: "Crypto",
  },
  {
    value: "CFDEquities",
    label: "CFD Equities",
  },
  {
    value: "Equities",
    label: "Equities",
  },
  {
    value: "USStocks",
    label: "US Stocks",
  },
  {
    value: "CryptoLeveraged",
    label: "Crypto Leveraged",
  },
  {
    value: "CryptoNonLeveraged",
    label: "Crypto Non Leveraged",
  },
];

const IbDetailedPerformanceReport = (props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [sizePerPage, setSizePerPage] = useState(10);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [productType, setProductType] = useState("");
  const [downloading, setDownloading] = useState(false);

  // Holds the filters from the last submitted search so that
  // pagination / page-size changes always re-use the same filters
  // rather than whatever the user may have typed but not yet submitted.
  const activeFilters = useRef({ 
    dateFrom: "", 
    dateTo: "", 
    productType: "" 
  });

  const {
    loading, docs, totals,
    page, totalDocs,
    hasNextPage, hasPrevPage, nextPage, prevPage,
  } = useSelector((state) => state.forex.ib.agreements.detailedPerformance);

  const fetchReport = (pg = 1, lmt = sizePerPage) => {
    dispatch(fetchDetailedPerformance({
      page: pg,
      limit: lmt,
      ...activeFilters.current,
    }));
  };

  const handleSubmit = () => {
    activeFilters.current = { 
      dateFrom, 
      dateTo, 
      productType 
    };
    fetchReport(1, sizePerPage);
  };

  // Re-fetch from page 1 when the user changes rows-per-page, but only
  // after at least one search has been performed (totalDocs > 0).
  useEffect(() => {
    if (totalDocs > 0) {
      fetchReport(1, sizePerPage);
    }
  }, [sizePerPage]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const blob = await downloadIbDetailedPerformanceAPI({
        dateFrom,
        dateTo,
        productType,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ib-detailed-performance.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // silently fail — user will notice missing download
    } finally {
      setDownloading(false);
    }
  };

  const columns = [
    {
      key: "clientName",
      text: t("Client Name"),
    },
    {
      key: "login",
      text: t("Login"),
    },
    {
      key: "productType",
      text: t("Product Type"),
    },
    {
      key: "closedLots",
      text: t("Total Closed Lots"),
    },
    {
      key: "openedLots",
      text: t("Total Opened Lots"),
    },
    {
      key: "rebateAmount",
      text: t("Total Rebate"),
    },
    {
      key: "commissionAmount",
      text: t("Total Commission"),
    },
    {
      key: "rebatePerLot",
      text: t("Rebate per Lot"),
    },
  ];

  return (
    <Container>
      <div className="page-content mt-5">
        <CardWrapper className="mt-3 px-5 py-4 glass-card shadow">
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="color-primary">{t("IB Detailed Performance Report")}</h3>
            <Button
              color="success"
              className="border-0"
              disabled={downloading || !dateFrom || !dateTo || !totalDocs}
              onClick={handleDownload}
            >
              {downloading ? <Spinner size="sm" className="me-2" /> : <i className="bx bx-download me-2" />}
              {t("Export to Excel")}
            </Button>
          </div>

          <AvForm onValidSubmit={handleSubmit}>
            <Row className="mt-3 align-items-end">
              <Col xs="12" md="3" lg="2">
                <AvField
                  type="date"
                  name="dateFrom"
                  label={t("Date From")}
                  className="mt-1 mb-2"
                  onChange={(e) => setDateFrom(e.target.value)}
                  max={dateTo ? moment(dateTo).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")}
                  required
                />
              </Col>
              <Col xs="12" md="3" lg="2">
                <AvField
                  type="date"
                  name="dateTo"
                  label={t("Date To")}
                  className="mt-1 mb-2"
                  onChange={(e) => setDateTo(e.target.value)}
                  max={moment().add(1, "days").format("YYYY-MM-DD")}
                  min={dateFrom && moment(dateFrom).format("YYYY-MM-DD")}
                  required
                />
              </Col>
              <Col xs="12" md="3">
                <AvField
                  type="select"
                  name="productType"
                  label={t("Product Type")}
                  className="mt-1 mb-2 form-select"
                  onChange={(e) => setProductType(e.target.value)}
                >
                  <option value="">{t("All")}</option>
                  {PRODUCT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </AvField>
              </Col>
              <Col xs="12" md="2">
                <Button
                  type="submit"
                  className="border-0 color-bg-btn mb-md-2 mt-3 mt-lg-0 w-100"
                >
                  {t("Search")}
                </Button>
              </Col>
            </Row>
          </AvForm>

          {totals && totalDocs > 0 && (
            <Row className="mt-4 g-3">
              {[
                { 
                  label: t("Total Closed Lots"), 
                  value: totals.totalClosedLots 
                },
                { 
                  label: t("Total Opened Lots"), 
                  value: totals.totalOpenedLots 
                },
                { 
                  label: t("Total Rebate"), 
                  value: totals.totalRebateAmount 
                },
                { 
                  label: t("Total Commission"), 
                  value: totals.totalCommissionAmount 
                },
              ].map(({ label, value }) => (
                <Col xs="12" sm="6" xl="3" key={label}>
                  <div className="border rounded-3 p-3 text-center">
                    <div className="text-muted mb-1" style={{ fontSize: 12 }}>{label}</div>
                    <div className="color-primary fw-bold fs-5">{value}</div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          <hr className="my-4" />

          <div className="mt-4 border rounded-3">
            <Table borderless responsive className="text-center mb-0">
              <Thead className="text-center table-light">
                <Tr>
                  {columns.map((col, i) => (
                    <Th data-priority={i} key={i} className="color-primary">
                      {col.text}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4">
                      <Spinner color="primary" />
                    </td>
                  </tr>
                ) : !docs || docs.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-muted">
                      {t("No data available")}
                    </td>
                  </tr>
                ) : (
                  docs.map((row, i) => (
                    <tr key={i} className="border-top">
                      {columns.map((col, j) => (
                        <td key={j}>{row[col.key] ?? "-"}</td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <div className="mt-4">
            <CustomPagination
              totalDocs={totalDocs}
              totalPages={Math.ceil(totalDocs / sizePerPage) || 0}
              page={parseInt(page) || 1}
              hasNextPage={hasNextPage}
              hasPrevPage={hasPrevPage}
              nextPage={nextPage}
              prevPage={prevPage}
              setSizePerPage={setSizePerPage}
              sizePerPage={sizePerPage}
              onChange={fetchReport}
            />
          </div>
        </CardWrapper>
      </div>
    </Container>
  );
};

export default withTranslation()(IbDetailedPerformanceReport);
